import type { CartState, ProductCard, ShowComparisonEnriched } from "@ai-guidance/shared";
import { CommerceOverlay, renderProductCards } from "./cards";
import { openComparisonModal, type ComparisonModal } from "./compare";
import { commerceText, type CommerceCopy } from "./text";
import { cartSectionIds, syncThemeCart, type CartMutationAction } from "./themeSync";
import type { CommerceAdapter, PriceBounds, SearchResults } from "./types";

/**
 * Partea de commerce a SDK-ului, văzută de executor și de captura de pagină:
 * adaptorul + ce se întâmplă în jurul lui (carduri, toast, badge-ul temei,
 * starea coșului pentru snapshot, markerul de eroare pentru tura următoare).
 *
 * Executorul rămâne subțire: el traduce acțiunea în apel, aici stă tot ce ține
 * de UI și de memorie între ture.
 */

/**
 * Unde își țin temele Shopify numărul de produse din coș. Nu există un contract
 * pentru asta - de-aia lista, nu un singur selector, și de-aia scrierea e
 * defensivă (vezi `updateCartBadges`). Primele două acoperă tema Warehouse a
 * POC-ului; restul sunt convențiile larg răspândite (Dawn și derivatele ei,
 * temele care marchează cu `data-`).
 *
 * Dacă nu se potrivește nimic, nu se întâmplă NIMIC: badge-ul rămâne cum era
 * până la prima navigare, iar agentul vede oricum coșul real în snapshot.
 */
export const CART_COUNT_SELECTORS = [
  "[data-cart-count]",
  ".header__cart-count",
  ".cart-count-bubble span[aria-hidden='true']",
  ".cart-count",
  ".cart-item-count",
  ".site-header__cart-count",
  "[data-cart-item-count]",
  ".js-cart-count",
];

/**
 * Scrie numărul de produse în badge-ul temei, fără să presupună nimic despre ea.
 *
 * Regula de siguranță: scriem DOAR peste un text gol sau pur numeric. Un badge
 * care zice „2 produse" sau „Coș (2)" e markup al clientului, iar înlocuirea lui
 * cu „3" ar strica pagina lui ca să economisim o reîncărcare. Acolo lăsăm cum e -
 * tema își va corecta singură numărul la prima navigare.
 */
export function updateCartBadges(count: number): void {
  for (const selector of CART_COUNT_SELECTORS) {
    let nodes: NodeListOf<Element>;
    try {
      nodes = document.querySelectorAll(selector);
    } catch {
      continue;
    }
    for (const node of nodes) {
      const current = (node.textContent ?? "").trim();
      if (current !== "" && !/^\d+$/.test(current)) continue;
      node.textContent = String(count);
    }
  }
}

/** Ce are nevoie runtime-ul de la widget - injectat, ca testele să nu monteze un widget întreg. */
export interface CommerceHost {
  /** Cardurile în firul conversației (chatul scris). */
  addCardsToThread: (node: HTMLElement) => void;
  /** Un nod în shadow root-ul widgetului (panoul plutitor, toast-ul). */
  mountOverlay: (node: HTMLElement) => void;
  /** Panoul de chat, deschis când răspunsul vine cu carduri. */
  openChat: () => void;
  /** În modul de voce cardurile plutesc peste pagină, nu în fir. */
  isVoiceActive: () => boolean;
  /**
   * O acțiune de coș a reușit. Opțional: singurul consumator e motorul de
   * proactivitate (un coș tocmai atins nu e un coș uitat), iar pe un magazin
   * fără proactivitate nu există nimeni care să asculte.
   */
  onCartChanged?: () => void;
  /**
   * O acțiune de coș a EȘUAT în browser (platforma a refuzat-o). Vizitatorul
   * vede toastul de eroare pe loc; hook-ul există ca și AGENTUL să vadă același
   * ecran imediat - vocea își împrospătează snapshotul de pe sesiune, unde
   * `lastActionError` tocmai s-a pus (sesiunea 27, 18 aug 2026: fără refresh,
   * eșecul ajungea la model abia la următoarea navigare, dacă mai venea una).
   */
  onCartActionFailed?: () => void;
  /**
   * Suprafața e orb-chatul (care poartă mereu pielea `hud`)? Singurul consumator e
   * modalul de comparație, care se deschide PESTE pagină și trebuie să arate ca
   * suprafața din care a plecat. Stă pe gazdă, nu pe runtime, fiindcă e o
   * proprietate a suprafeței - runtime-ul doar o citește.
   */
  hudSkin?: boolean;
}

/** Cât așteaptă o captură de pagină după starea proaspătă a coșului. */
export const CART_SNAPSHOT_TIMEOUT_MS = 800;

/** Pauza dinaintea reload-ului pe pagina coșului - cât să se vadă toast-ul. */
export const CART_PAGE_RELOAD_DELAY_MS = 600;

export class CommerceRuntime {
  private readonly overlay: CommerceOverlay;
  /** Ultima stare citită - fallback când /cart.js întârzie peste plafonul capturii. */
  private cart: CartState | null = null;
  /** Eșecul nelivrat încă agentului; se consumă la prima captură care îl trimite. */
  private pendingError: string | null = null;
  /** Modalul de comparație deschis acum, dacă există (unul singur - vezi `showComparison`). */
  private comparison: ComparisonModal | null = null;

  constructor(
    private readonly adapter: CommerceAdapter,
    private readonly host: CommerceHost,
    /** Textele de commerce în limba appului - vezi `commerce/text.ts`. */
    private readonly copy: CommerceCopy = commerceText(),
  ) {
    this.overlay = new CommerceOverlay(host, copy);
  }

  cartPath(): string {
    return this.adapter.cartPath();
  }

  /**
   * `open_search`: calea de rezultate a magazinului, compusă de adaptor -
   * inclusiv filtrele de preț, când vizitatorul și-a rostit bugetul.
   */
  searchPath(query: string, bounds?: PriceBounds): string {
    return this.adapter.searchPath(query, bounds);
  }

  /**
   * `show_products` (forma îmbogățită de backend). Butonul „Adaugă în coș" de pe
   * card cheamă direct adaptorul: click-ul E confirmarea umană, deci nu mai are
   * nevoie de o tură de model ca să se întâmple.
   */
  showProducts(cards: ProductCard[]): void {
    const node = renderProductCards(cards, { onAdd: (card) => void this.addToCart(card.variantId, 1) }, this.copy);
    if (this.host.isVoiceActive()) {
      this.overlay.showCards(node);
      return;
    }
    this.host.addCardsToThread(node);
    this.host.openChat();
  }

  /**
   * `show_comparison` (forma îmbogățită de backend). Spre deosebire de
   * `show_products`, comparația NU intră în panoul de recomandate pe nicio
   * suprafață: își deschide propriul modal, centrat pe pagină, prin singurul
   * cârlig comun celor trei suprafețe (`mountOverlay`). Deci nu deschide chatul
   * și nu depinde de starea vocii - vezi antetul din `compare.ts`.
   *
   * O singură comparație pe ecran: una nouă o înlocuiește pe cea veche, altfel
   * două tabele s-ar suprapune fix în centru.
   */
  showComparison(data: ShowComparisonEnriched): void {
    this.comparison?.close();
    this.comparison = openComparisonModal(
      data,
      this.copy,
      (node) => this.host.mountOverlay(node),
      this.host.hudSkin === true,
    );
  }

  async addToCart(variantId: string, quantity: number): Promise<void> {
    const result = await this.adapter.addToCart(variantId, quantity, mutationContext(this.adapter.platform));
    await this.settle(result, "add_to_cart", variantId, this.copy.added, { action: "add", quantity });
  }

  async setQuantity(variantId: string, quantity: number): Promise<void> {
    const result = await this.adapter.setQuantity(variantId, quantity, mutationContext(this.adapter.platform));
    const done = quantity === 0 ? this.copy.removed : this.copy.quantityUpdated;
    const action: CartMutationAction = quantity === 0 ? "remove" : "update";
    await this.settle(result, "set_cart_quantity", variantId, done, { action, quantity });
  }

  /**
   * Coada comună a celor două acțiuni de coș: toast, badge, marker de eroare.
   * La succes recitim /cart.js - numărul din badge trebuie să vină de la
   * platformă, nu dintr-o adunare făcută de noi peste o stare care putea fi deja
   * schimbată în alt tab.
   */
  private async settle(
    result: { ok: true; sections?: Record<string, string> } | { ok: false; message: string },
    action: string,
    variantId: string,
    okText: string,
    mutation: { action: CartMutationAction; quantity: number },
  ): Promise<void> {
    if (!result.ok) {
      this.pendingError = `${action} ${variantId}: ${result.message}`.slice(0, 200);
      this.overlay.showToast(result.message, "error");
      // DUPĂ ce markerul de eroare e pus: împrospătarea capturează exact
      // ecranul pe care îl vede omul - cu eșecul în el.
      this.host.onCartActionFailed?.();
      return;
    }
    // Panoul plutitor a fost sugestia; acțiunea îl face inutil.
    this.overlay.hideCards();
    this.overlay.showToast(okText);
    // Înaintea recitirii coșului, nu după: mutația a REUȘIT deja, iar dacă
    // /cart.js n-ar răspunde (return-ul de mai jos) semnalul s-ar pierde.
    this.host.onCartChanged?.();
    const cart = await this.adapter.getCart();
    if (!cart) return;
    this.cart = cart;
    updateCartBadges(cart.itemCount);
    /**
     * Sertarul de coș al temei se randează din starea EI, deci nu află de
     * mutațiile noastre prin fetch. `syncThemeCart` i-o spune în limba ei; vezi
     * `themeSync.ts` pentru mecanismul citit din sursa temei.
     *
     * Când tema e recunoscută, ea își re-randează singură ȘI sertarul, ȘI pagina
     * de coș - deci reload-ul devine redundant și îl sărim: e o navigare întreagă
     * plătită degeaba, care în plus pierde poziția în pagină. Îl păstrăm exact
     * pentru cazul invers (temă necunoscută, `handled = false`), unde e singurul
     * lucru care mai funcționează.
     */
    const handled = syncThemeCart({
      action: mutation.action,
      // Platforma vine de pe ADAPTOR, nu din config: runtime-ul nu vede configul,
      // iar adaptorul e chiar lucrul care știe cu ce magazin vorbește.
      platform: this.adapter.platform,
      variantId,
      quantity: mutation.quantity,
      itemCount: cart.itemCount,
      ...(result.sections ? { sections: result.sections } : {}),
    });
    if (!handled) this.maybeRefreshCartPage();
  }

  /**
   * Pagina de coș e randată de temă la ÎNCĂRCARE: o modificare prin API lasă
   * lista veche pe ecran (badge-ul îl scriem noi, conținutul nu - observat live
   * pe demo3, 9 aug 2026). Fix: pe pagina coșului reîncărcăm după acțiune.
   * PLASĂ DE SIGURANȚĂ, nu drum principal: se cheamă doar când `syncThemeCart`
   * n-a recunoscut tema (vezi `settle`). Pe temele acoperite, tema își morfează
   * singură secțiunea și nu mai are ce reîncărca.
   * NU și în conversație vocală - un reload ar tăia apelul; acolo badge-ul și
   * confirmarea vorbită ajung, iar pagina se prinde la următoarea navigare.
   * `endsWith`, nu egalitate: magazinele multi-limbă prefixează calea
   * (`/en/cart`), iar `cartPath` din config rămâne `/cart`.
   */
  private maybeRefreshCartPage(): void {
    if (this.host.isVoiceActive()) return;
    const path = location.pathname;
    const cartPath = this.adapter.cartPath();
    if (path !== cartPath && !path.endsWith(cartPath)) return;
    // Mică pauză ca toast-ul de confirmare să apuce să se vadă.
    window.setTimeout(() => location.reload(), CART_PAGE_RELOAD_DELAY_MS);
  }

  /**
   * Coșul REAL, citit acum din magazin, cu actualizarea cache-ului intern.
   * Folosit de motorul de proactivitate (docs/plan-commerce-proactivity.md):
   * `stale_cart` și `exit_intent` vorbesc despre coș, iar vizitatorul îl poate
   * umple din tema magazinului, fără să treacă prin noi.
   *
   * Separată de `snapshotExtras`: aceea consumă markerul de eroare și are un
   * plafon de timp fiindcă ține o întrebare în loc. Aici nu așteaptă nimeni -
   * o citire lentă ajunge pur și simplu la tick-ul următor. La eșec întoarce
   * ultima stare cunoscută, niciodată `{}`: un nudge care afirmă „coș gol"
   * fiindcă rețeaua a clipit e mai rău decât niciun nudge.
   */
  async readCart(): Promise<CartState | null> {
    const fresh = await this.adapter.getCart();
    if (fresh) this.cart = fresh;
    return fresh ?? this.cart;
  }

  /**
   * Ce se atașează la captura de pagină (`snapshot.cart` + `snapshot.lastActionError`
   * + `snapshot.searchResults`).
   *
   * Captura nu se blochează pe rețea: peste plafon cade pe ultima stare
   * cunoscută. Un coș ușor învechit e mult mai bun decât o întrebare întârziată
   * cu secunde - iar acțiunile noastre reîmprospătează oricum starea.
   */
  async snapshotExtras(
    timeoutMs = CART_SNAPSHOT_TIMEOUT_MS,
  ): Promise<{ cart?: CartState; lastActionError?: string; searchResults?: SearchResults }> {
    const fresh = await withTimeout(this.adapter.getCart(), timeoutMs);
    if (fresh) this.cart = fresh;
    const cart = fresh ?? this.cart;
    const error = this.pendingError;
    // Se consumă aici: agentul îl vede o singură dată, în tura următoare, și
    // corectează verbal. Un marker repetat ar face agentul să se scuze la
    // nesfârșit pentru o adăugare eșuată acum trei ture.
    this.pendingError = null;
    // Citire pur DOM, sincronă și fără rețea: nu are ce întârzia captura. E
    // împachetată totuși, fiindcă un adaptor care aruncă pe un markup ciudat
    // n-are voie să lase întrebarea fără coș.
    let searchResults: SearchResults | undefined;
    try {
      searchResults = this.adapter.searchResults?.();
    } catch {
      searchResults = undefined;
    }
    return {
      ...(cart ? { cart } : {}),
      ...(error ? { lastActionError: error } : {}),
      ...(searchResults ? { searchResults } : {}),
    };
  }
}

/**
 * Ce cerem în plus de la mutație: secțiunile temei, re-randate în același
 * răspuns. Id-urile se citesc ÎNAINTE de mutație - după ea, un morph al temei
 * le-ar putea deja înlocui în DOM.
 */
function mutationContext(platform: CommerceAdapter["platform"]) {
  // Secțiunile sunt un mecanism Shopify (`sections=` pe /cart/add.js). Pe Woo
  // nu există nimic de cerut, iar o interogare de DOM pe fiecare mutație ar fi
  // muncă degeaba.
  if (platform !== "shopify") return {};
  const sections = cartSectionIds();
  return sections.length > 0 ? { sections, sectionsUrl: location.pathname } : {};
}

/** `null` dacă promisiunea n-a apucat să se încheie în timpul dat. */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), ms);
    void promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch(() => {
        clearTimeout(timer);
        resolve(null);
      });
  });
}
